import { ElementRef, Injectable } from '@angular/core';
import get from 'lodash-es/get';

import { ParallaxBackgroundService } from '@finder/shared/components/parallax-background/parallax-background.service';
import { ParallaxThemeInterface } from './parallax-theme.inferface';

@Injectable()
export class SwgeTheme implements ParallaxThemeInterface {
    private backgroundLayer;
    private animationLayer;
    private staticLayer;
    private parallaxBgService;

    constructor(
        private elementRef: ElementRef,
    ) {}

    /**
     * @name        setup
     * @description Allow the service to update the layers and anything else it needs
     * @param       elementRef Reference to the parallax component
     * @param       parallaxBgService Reference to service that contains data we can use
     */
    setup(parallaxBgService: ParallaxBackgroundService) {
        this.parallaxBgService = parallaxBgService;
        const blockData = parallaxBgService.data;

        // We need to get a reference to each of the layers and add some default classes
        this.backgroundLayer = this.elementRef.nativeElement.querySelector('.layer-1');
        this.animationLayer = this.elementRef.nativeElement.querySelector('.layer-2');
        this.staticLayer = this.elementRef.nativeElement.querySelector('.layer-3');

        // Add some default classes
        this.backgroundLayer.classList.add('fixed-background', 'stars');
        this.animationLayer.classList.add('clouds', 'hide-clouds');
        this.staticLayer.classList.add('clouds', 'hide-clouds');

        const staticBg = get(blockData, 'static.srcGroup', [{srcSet: ''}])[0].srcSet;
        const dynamicBg = get(blockData, 'dynamic.srcGroup', [{srcSet: ''}])[0].srcSet;

        // Add a few styles to the head
        const css = `
            finder-parallax-background .stars {
                background-image: url(${staticBg});
            }

            finder-parallax-background .clouds {
                background-image: linear-gradient(0deg, white 15%, rgba(255, 255, 255, 0) 35%), url(${dynamicBg});
            }
        `;
        parallaxBgService.createStyle(css, 'parallax-theme');
    }

    intersectionCleanup(element) {
        // Clean up listeners on the pin action
        this.parallaxBgService.cancelScrollWatch(element);

        // Clean up the bit of white at the end caused by the pin.
        // Only when scrolling back to the top
        if (this.parallaxBgService.viewport.delta > 0) {
            this.backgroundLayer.removeAttribute('style');
        }
    }

    /**
     * @name        handleIntersection
     * @description Handle the intersection event
     * @param       ioEntry IntersectionObserver entry object
     * @param       this.parallaxBgService This is passed as a param to avoid a circular dependency issue
     */
    handleIntersection(ioEntry: IntersectionObserverEntry) {
        // Pull the element from the entry
        const element = ioEntry.target as HTMLElement;

        // Chance for clean up
        if (!ioEntry.isIntersecting && element.dataset.scroll === 'pin') {
            this.intersectionCleanup(element);

            return;
        }

        // Figure out what direction we're going. We only need a small sample of data
        this.parallaxBgService.updateViewport();

        // We don't know the direction the page is scrolling yet.
        if (this.parallaxBgService.viewport.delta === 0) {
            return;
        }

        /**
         * Check to see if we're doing a pin transition
         */
        switch (element.dataset.scroll) {
            case 'pin':
                this.parallaxBgService.startScrollWatch(element, this.backgroundLayer);
                break;
            case 'transition':
                this.handleTransition(this.parallaxBgService.transition(element));
                break;
            default:
                break;
        }
    }

    handleTransition(animation) {
        switch (animation) {
            case 'show-clouds':
                this.showCloudAnimation();
                break;
            case 'show-stars':
                this.showStarsAnimation();
                break;
            default:
                break;
        }
    }

    showStarsAnimation() {
        // Remove class from body that changes header colors
        this.elementRef.nativeElement.closest('body').classList.remove('clouds-active');

        this.animationLayer.classList.add('hide-clouds');
        this.animationLayer.classList.remove('animate-clouds');

        this.staticLayer.classList.add('hide-clouds');
        this.staticLayer.classList.remove('static');
    }

    showCloudAnimation() {
        // Add new class to the body to change header colors
        this.elementRef.nativeElement.closest('body').classList.add('clouds-active');

        // Display clouds
        this.animationLayer.classList.remove('hide-clouds');
        this.animationLayer.classList.add('animate-clouds');

        // Set the clouds on the background so the seam doesnt show
        // and we have a clean transition
        this.staticLayer.classList.remove('hide-clouds');
        this.staticLayer.classList.add('static');
    }
}
